import React, { useEffect, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import TextField from '@mui/material/TextField';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AppTabs from '../../../components/AppTabs';
import BaseTanStackTable from '../../../components/BaseTanStackTable';
import SnackbarAlert from '../../../components/SnackbarAlert';
import { useModal } from '../../../hooks/useModal';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { useTableState } from '../../../hooks/useTableState';
import { FundosGerenciadosData } from '../../../types/fundosGerenciados';
import {
  editarFundo,
  deleteFundoGerenciado,
  postFundoSucessao,
  getFundoSucessaoComoOrigem,
  deleteFundoSucessaoComoOrigem,
} from '../../../services/fundosGerenciadosService';
import { FundoSucessao } from '../../../types/fundosGerenciados';
import DetailFundoModal from '../modals/DetailFundoModal';
import EditFundoModal from '../modals/EditFundoModal';
import CarteiraPosicaoModal from '../modals/CarteiraPosicaoModal';

interface TableFundosProps {
  columns: ColumnDef<FundosGerenciadosData>[];
  data: FundosGerenciadosData[];
}

const TableFundos: React.FC<TableFundosProps> = ({ columns, data }) => {
  const [updatedData, setUpdatedData] = useState<FundosGerenciadosData[]>(data);
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive'>('active');

  // Deactivation dialog state
  const [deactivateTarget, setDeactivateTarget] = useState<FundosGerenciadosData | null>(null);
  const [transferEnabled, setTransferEnabled] = useState(false);
  const [destFundoId, setDestFundoId] = useState<number | ''>('');
  const [dataTransferencia, setDataTransferencia] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  // Reactivation dialog state
  const [reactivateTarget, setReactivateTarget] = useState<FundosGerenciadosData | null>(null);
  const [reactivateSucessao, setReactivateSucessao] = useState<FundoSucessao | null>(null);
  const [reactivating, setReactivating] = useState(false);

  const { pagination, setPagination, sorting, setSorting, globalFilter, handleFilterChange } = useTableState(10);
  const { snackbar, showSuccess, showError, close: closeSnackbar } = useSnackbar();

  const detailModal  = useModal<FundosGerenciadosData>();
  const editModal    = useModal<FundosGerenciadosData>();
  const posicaoModal = useModal<FundosGerenciadosData>();

  useEffect(() => { setUpdatedData(data); }, [data]);

  const filteredData = React.useMemo(
    () => updatedData.filter((f) => statusFilter === 'active' ? f.active : !f.active),
    [updatedData, statusFilter]
  );

  const activeFunds = React.useMemo(
    () => updatedData.filter(f => f.active),
    [updatedData]
  );

  const handleDelete = async (id: number) => {
    try {
      await deleteFundoGerenciado(id);
      setUpdatedData(prev => prev.filter(f => f.id !== id));
      showSuccess('Fundo deletado com sucesso!');
    } catch {
      showError('Erro ao deletar fundo. Por favor, tente novamente.');
    }
  };

  const handleSave = async (updatedDetails: FundosGerenciadosData) => {
    try {
      const saved = await editarFundo(updatedDetails);
      setUpdatedData(prev => prev.map(f => f.id === saved.id ? saved : f));
      editModal.close();
    } catch {
      showError('Erro ao salvar as alterações. Tente novamente.');
    }
  };

  const handleToggleActive = async (fundo: FundosGerenciadosData) => {
    try {
      const saved = await editarFundo({ ...fundo, active: !fundo.active });
      setUpdatedData(prev => prev.map(f => f.id === saved.id ? saved : f));
      showSuccess(saved.active ? 'Fundo ativado com sucesso!' : 'Fundo desativado com sucesso!');
    } catch {
      showError('Erro ao alterar status do fundo. Por favor, tente novamente.');
    }
  };

  const handleToggleClick = async (row: FundosGerenciadosData) => {
    if (row.active) {
      setDeactivateTarget(row);
      setTransferEnabled(false);
      setDestFundoId('');
      setDataTransferencia('');
    } else {
      // Check if this fund has a succession record as origin
      const suc = await getFundoSucessaoComoOrigem(row.id).catch(() => null);
      if (suc) {
        setReactivateTarget(row);
        setReactivateSucessao(suc);
      } else {
        handleToggleActive(row);
      }
    }
  };

  const handleReactivateClose = () => {
    setReactivateTarget(null);
    setReactivateSucessao(null);
  };

  const handleReactivateConfirm = async () => {
    if (!reactivateTarget) return;
    setReactivating(true);
    try {
      await deleteFundoSucessaoComoOrigem(reactivateTarget.id);
      await handleToggleActive(reactivateTarget);
      handleReactivateClose();
    } catch {
      showError('Erro ao reativar fundo. Por favor, tente novamente.');
    } finally {
      setReactivating(false);
    }
  };

  const handleDeactivateClose = () => {
    setDeactivateTarget(null);
    setTransferEnabled(false);
    setDestFundoId('');
    setDataTransferencia('');
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await handleToggleActive(deactivateTarget);
      if (transferEnabled && destFundoId !== '' && dataTransferencia) {
        await postFundoSucessao(destFundoId as number, deactivateTarget.id, dataTransferencia);
        showSuccess('Fundo desativado e transferência de carteira registrada.');
      }
      handleDeactivateClose();
    } catch {
      showError('Erro ao processar a desativação. Por favor, tente novamente.');
    } finally {
      setDeactivating(false);
    }
  };

  const transferValid = !transferEnabled || (destFundoId !== '' && dataTransferencia !== '');

  return (
    <>
      <AppTabs value={statusFilter} onChange={(_, v) => setStatusFilter(v)} sx={{ mb: 2 }}>
        <Tab label="Ativos"   value="active"   />
        <Tab label="Inativos" value="inactive" />
      </AppTabs>

      <BaseTanStackTable
        columns={columns}
        data={filteredData}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={handleFilterChange}
        manualPagination={false}
        manualSorting={false}
        toolbar={
          <TextField
            value={globalFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            placeholder="Buscar na tabela..."
            variant="outlined"
            size="small"
            style={{ width: '300px' }}
          />
        }
        renderActions={(row) => (
          <>
            <Tooltip title="Ver detalhes" placement="top">
              <VisibilityIcon
                onClick={() => detailModal.open(row)}
                style={{ cursor: 'pointer', marginRight: '8px' }}
              />
            </Tooltip>
            <Tooltip title="Ver posição da carteira" placement="top">
              <AccountBalanceIcon
                onClick={() => posicaoModal.open(row)}
                style={{ cursor: 'pointer', marginRight: '8px', color: '#2C91AD' }}
              />
            </Tooltip>
            <Tooltip title="Editar fundo" placement="top">
              <EditIcon
                onClick={() => editModal.open(row)}
                style={{ cursor: 'pointer', marginRight: '8px' }}
              />
            </Tooltip>
            <Tooltip title="Excluir fundo" placement="top">
              <DeleteIcon
                onClick={() => handleDelete(row.id)}
                style={{ cursor: 'pointer', color: '#E51739', marginRight: '8px' }}
              />
            </Tooltip>
            <Tooltip title={row.active ? 'Desativar fundo' : 'Ativar fundo'} placement="top">
              <IconButton
                onClick={() => handleToggleClick(row)}
                size="small"
                aria-label={row.active ? 'Desativar fundo' : 'Ativar fundo'}
              >
                {row.active
                  ? <ToggleOnIcon  style={{ color: '#29A152' }} />
                  : <ToggleOffIcon style={{ color: '#8A98A8' }} />}
              </IconButton>
            </Tooltip>
          </>
        )}
      />

      {/* Deactivation dialog */}
      <Dialog open={!!deactivateTarget} onClose={handleDeactivateClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter', fontSize: '16px', fontWeight: 600 }}>
          Desativar fundo
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#2D3748' }}>
            Tem certeza que deseja desativar o fundo{' '}
            <strong>{deactivateTarget?.fantasia || deactivateTarget?.razao_social}</strong>?
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={transferEnabled}
                onChange={(_, v) => setTransferEnabled(v)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontFamily: 'Inter', fontSize: '13px' }}>
                Registrar transferência de carteira para outro fundo
              </Typography>
            }
          />

          {transferEnabled && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontFamily: 'Inter', fontSize: '13px' }}>Fundo destino</InputLabel>
                <Select
                  value={destFundoId}
                  label="Fundo destino"
                  onChange={(e) => setDestFundoId(e.target.value as number)}
                  sx={{ fontFamily: 'Inter', fontSize: '13px' }}
                >
                  {activeFunds
                    .filter(f => f.id !== deactivateTarget?.id)
                    .map(f => (
                      <MenuItem key={f.id} value={f.id} sx={{ fontFamily: 'Inter', fontSize: '13px' }}>
                        {f.fantasia || f.razao_social}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              <TextField
                label="Data de transferência"
                type="date"
                size="small"
                fullWidth
                value={dataTransferencia}
                onChange={(e) => setDataTransferencia(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { fontFamily: 'Inter', fontSize: '13px' } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeactivateClose}
            disabled={deactivating}
            sx={{ fontFamily: 'Inter', fontSize: '13px', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeactivateConfirm}
            disabled={deactivating || !transferValid}
            variant="contained"
            color="error"
            sx={{ fontFamily: 'Inter', fontSize: '13px', textTransform: 'none', minWidth: 100 }}
          >
            {deactivating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivation dialog */}
      <Dialog open={!!reactivateTarget} onClose={handleReactivateClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter', fontSize: '16px', fontWeight: 600 }}>
          Reativar fundo
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '16px !important' }}>
          <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#2D3748' }}>
            O fundo <strong>{reactivateTarget?.fantasia || reactivateTarget?.razao_social}</strong> possui
            um registro de transferência de carteira para{' '}
            <strong>{reactivateSucessao?.fundo_destino_nome}</strong>.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter', fontSize: '13px', color: '#6B7A8D' }}>
            Ao reativar, esse registro será removido automaticamente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleReactivateClose}
            disabled={reactivating}
            sx={{ fontFamily: 'Inter', fontSize: '13px', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleReactivateConfirm()}
            disabled={reactivating}
            variant="contained"
            sx={{ fontFamily: 'Inter', fontSize: '13px', textTransform: 'none', minWidth: 100 }}
          >
            {reactivating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {detailModal.isOpen && detailModal.item && (
        <DetailFundoModal
          isOpen={detailModal.isOpen}
          onRequestClose={detailModal.close}
          details={detailModal.item}
        />
      )}
      {editModal.isOpen && editModal.item && (
        <EditFundoModal
          isOpen={editModal.isOpen}
          onRequestClose={editModal.close}
          details={editModal.item}
          onSave={handleSave}
        />
      )}
      {posicaoModal.isOpen && posicaoModal.item && (
        <CarteiraPosicaoModal
          isOpen={posicaoModal.isOpen}
          onRequestClose={posicaoModal.close}
          fundoId={posicaoModal.item.id}
          fundoNome={posicaoModal.item.fantasia || ''}
        />
      )}

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </>
  );
};

export default TableFundos;
